import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';
import { SHIRES, type ShireId } from '../components/shared/electorate-shires';

type ContactSearch = { from?: ShireId };

const VALID_SHIRE_IDS = new Set<string>(SHIRES.map((s) => s.id));

export const Route = createFileRoute('/contact')({
  component: ContactPage,
  validateSearch: (search: Record<string, unknown>): ContactSearch => {
    const from = typeof search.from === 'string' && VALID_SHIRE_IDS.has(search.from)
      ? (search.from as ShireId)
      : undefined;
    return from ? { from } : {};
  },
  head: () => ({
    meta: [
      { title: 'Contact Bevan Eatts MP — Warren-Blackwood' },
      {
        name: 'description',
        content:
          'Contact Bevan Eatts MP, Member for Warren-Blackwood: phone, email, office hours, and what your MP can help with vs your shire council.',
      },
      { property: 'og:title', content: 'Contact Bevan Eatts MP — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'How to reach Bevan Eatts MP — phone, email, office hours, and the kinds of issues his office can help you with.',
      },
    ],
  }),
});

const card: React.CSSProperties = {
  background: 'var(--cream)',
  border: '1px solid var(--rule)',
  borderRadius: 8,
  padding: '1.1rem 1.2rem',
  marginBottom: 'var(--sp-md)',
};

const dt: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '0.85rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--ink-mid)',
  marginBottom: '0.2rem',
};

const dd: React.CSSProperties = {
  margin: '0 0 0.95rem',
  fontSize: '1rem',
  color: 'var(--ink)',
  lineHeight: 1.45,
};

const link: React.CSSProperties = {
  color: 'var(--rivergum)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

const h2: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '1.2rem',
  color: 'var(--green)',
  margin: '0 0 0.7rem',
};

const list: React.CSSProperties = {
  paddingLeft: '1.2rem',
  margin: 0,
  fontSize: '0.97rem',
  lineHeight: 1.55,
  color: 'var(--ink)',
};

function ContactPage() {
  const { from } = useSearch({ from: '/contact' });
  const fromShire = from ? SHIRES.find((s) => s.id === from) : undefined;

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <Breadcrumbs
        items={
          fromShire
            ? [
                { label: 'Map', to: '/' },
                { label: fromShire.name, to: `/shire/${fromShire.id}` },
                { label: 'Contact' },
              ]
            : [{ label: 'Map', to: '/' }, { label: 'Contact' }]
        }
      />

      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.7rem, 4.5vw, 2.2rem)',
          color: 'var(--green)',
          margin: '0.4rem 0 0.3rem',
        }}
      >
        Contact Bevan Eatts MP
      </h1>
      <p style={{ color: 'var(--ink-mid)', margin: '0 0 var(--sp-md)', fontSize: '1rem' }}>
        Member for Warren-Blackwood · Legislative Assembly · The Nationals WA
      </p>

      {fromShire && (
        <aside
          aria-label={`Contacting Bevan about ${fromShire.name}`}
          style={{
            background: 'var(--green-pale)',
            borderLeft: '4px solid var(--rivergum)',
            borderRadius: 6,
            padding: '0.9rem 1.05rem',
            margin: '0 0 var(--sp-md)',
          }}
        >
          <p
            style={{
              margin: '0 0 0.45rem',
              fontFamily: 'var(--font-heading)',
              fontSize: '1.05rem',
              color: 'var(--green)',
            }}
          >
            You're contacting Bevan about the Shire of {fromShire.name}.
          </p>
          <p style={{ margin: '0 0 0.45rem', fontSize: '0.95rem', color: 'var(--ink)', lineHeight: 1.5 }}>
            As your state MP, Bevan can help with WA state government matters
            affecting {fromShire.name} — hospitals and WA Country Health Service,
            state schools, Main Roads WA, WA Police, housing, and raising local
            issues in the Legislative Assembly.
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            <Link
              to="/shire/$name"
              params={{ name: fromShire.id }}
              style={link}
            >
              ← Back to the {fromShire.name} shire page
            </Link>
          </p>
        </aside>
      )}

      <p
        style={{
          fontSize: '1.05rem',
          lineHeight: 1.55,
          color: 'var(--ink)',
          margin: '0 0 var(--sp-md)',
        }}
      >
        Whether you're navigating a state government department, raising a local
        concern, or just want to share your view — every enquiry is read, and
        every constituent gets a reply. Here's the quickest way to reach the
        office, and what to expect when you do.
      </p>

      <section aria-labelledby="reach" style={card}>
        <h2 id="reach" style={h2}>How to reach the office</h2>
        <dl style={{ margin: 0 }}>
          <dt style={dt}>Phone</dt>
          <dd style={dd}>
            <a href="tel:+61898483171" style={link}>(08) 9848 3171</a>
            <span style={{ color: 'var(--ink-mid)', fontSize: '0.9rem' }}>
              {' '}— fastest for urgent matters
            </span>
          </dd>

          <dt style={dt}>Email</dt>
          <dd style={dd}>
            <a href="mailto:bevan.eatts@mp.wa.gov.au" style={link}>
              bevan.eatts@mp.wa.gov.au
            </a>
            <span style={{ color: 'var(--ink-mid)', fontSize: '0.9rem' }}>
              {' '}— best for detailed enquiries with documents attached
            </span>
          </dd>

          <dt style={dt}>Electorate office</dt>
          <dd style={dd}>
            5/78 Strickland Street<br />
            Denmark WA 6333
          </dd>

          <dt style={dt}>Postal address</dt>
          <dd style={{ ...dd, marginBottom: 0 }}>
            PO Box 327<br />
            Denmark WA 6333
          </dd>
        </dl>
      </section>

      <section aria-labelledby="hours" style={card}>
        <h2 id="hours" style={h2}>Office hours</h2>
        <p style={{ margin: '0 0 0.55rem', fontSize: '1rem', color: 'var(--ink)' }}>
          <strong>Monday to Friday, 9:00 am – 4:30 pm</strong> (closed public
          holidays).
        </p>
        <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          Bevan and the team travel across the electorate regularly, so if you'd
          like an in-person meeting it's best to phone or email ahead. Mobile
          office visits to towns outside Denmark are scheduled throughout the
          year — call the office to ask when he's next in your shire.
        </p>
      </section>

      <section aria-labelledby="how-to" style={card}>
        <h2 id="how-to" style={h2}>How to submit an enquiry</h2>
        <ol style={{ ...list, paddingLeft: '1.3rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>
            <strong>Tell us who you are.</strong> Your name, the town or shire
            you live in, and the best phone or email to reply on.
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <strong>Describe the issue plainly.</strong> A few sentences is
            enough — what's happened, when, and who else you've already spoken
            to (an agency, your council, a service provider).
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <strong>Attach anything relevant.</strong> Reference numbers, letters
            you've received, photos — they help the office act quickly on your
            behalf.
          </li>
          <li>
            <strong>Say what outcome you're hoping for.</strong> Even "I just
            want someone to look at this" is a clear ask.
          </li>
        </ol>
      </section>

      <section aria-labelledby="response" style={card}>
        <h2 id="response" style={h2}>What response time to expect</h2>
        <ul style={list}>
          <li style={{ marginBottom: '0.45rem' }}>
            <strong>Phone calls:</strong> answered during office hours, or
            returned the same or next business day.
          </li>
          <li style={{ marginBottom: '0.45rem' }}>
            <strong>Email enquiries:</strong> an acknowledgement within{' '}
            <strong>2 business days</strong>, with a substantive reply or
            referral within <strong>10 business days</strong>.
          </li>
          <li>
            <strong>Casework involving another agency:</strong> can take longer
            — the office will keep you updated as the matter progresses.
          </li>
        </ul>
        <p
          style={{
            margin: '0.85rem 0 0',
            padding: '0.7rem 0.85rem',
            background: 'var(--green-pale)',
            borderLeft: '3px solid var(--green)',
            borderRadius: 4,
            fontSize: '0.93rem',
            color: 'var(--ink)',
            lineHeight: 1.5,
          }}
        >
          If your matter is genuinely urgent — a safety risk, a deadline today
          or tomorrow — please <strong>phone</strong> rather than email so the
          team can triage straight away.
        </p>
      </section>

      <section aria-labelledby="what-with" style={card}>
        <h2 id="what-with" style={h2}>What your MP can help with</h2>
        <p style={{ margin: '0 0 0.65rem', fontSize: '0.97rem', color: 'var(--ink)', lineHeight: 1.5 }}>
          As your state Member of Parliament, Bevan can advocate on matters
          handled by <strong>WA state government</strong> agencies:
        </p>
        <ul style={list}>
          <li>Hospitals and WA Country Health Service</li>
          <li>State school enrolments, funding and facilities</li>
          <li>Main Roads WA, WA Police, the Housing Authority</li>
          <li>TAFE colleges and planning appeals to the State Administrative Tribunal</li>
          <li>Land tenure, water licensing, environment and biosecurity</li>
          <li>Raising local issues directly in the Legislative Assembly</li>
        </ul>

        <h3
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1rem',
            color: 'var(--green)',
            margin: '1.1rem 0 0.4rem',
          }}
        >
          When to talk to your shire council instead
        </h3>
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.97rem', color: 'var(--ink)', lineHeight: 1.5 }}>
          Local councils — not the state MP — handle roads in town, rubbish and
          recycling, dogs and rangers, building approvals, local parks and most
          community facilities. Each shire page on this site has direct council
          contact details:
        </p>
        <p style={{ margin: 0, fontSize: '0.97rem' }}>
          <Link to="/" style={link}>
            Open the electorate map →
          </Link>
        </p>
      </section>

      <p
        style={{
          marginTop: 'var(--sp-md)',
          fontSize: '0.85rem',
          color: 'var(--ink-mid)',
          textAlign: 'center',
          fontStyle: 'italic',
        }}
      >
        Authorised by B. Eatts, 5/78 Strickland Street, Denmark WA 6333.
      </p>
    </main>
  );
}
